import React from 'react';
import PropTypes from 'prop-types';
import Wrapper from './Wrapper';
import Trigger from './Trigger';
import RoomList from './RoomList';
import { occupancyShape } from 'Constants/PropTypes';
import Icon from 'Components/Icon';
import WidgetContext from 'State/WidgetContext';

/**
 * OccupancySelector
 *
 * It gives the user the controls to establish the range of ages and number of
 * occupants that will be in the room, according to what has been configured.
 *
 * If the max age for children is -1, the ages array will be filled with -1.
 */

class OccupancySelector extends React.Component {
    constructor(props) {
        super(props);

        this.wrapperRef = React.createRef();
        this.triggerRef = React.createRef();

        this.state = { isActive: false };

        this.handleClick = this.handleClick.bind(this);
        this.showOccupancySelector = this.showOccupancySelector.bind(this);
        this.closeButton = this.closeButton.bind(this);
        this.recalculatePopupDimensions = this.recalculatePopupDimensions.bind(this);
    }

    /**
     * The component receives information about the clicks and if it was outside of the
     * OccupancySelector or in the trigger then the selector is closed in case it is open
     */
    componentDidMount() {
        document.addEventListener('mousedown', this.handleClick);
    }

    componentWillUnmount() {
        document.removeEventListener('mousedown', this.handleClick);
    }

    recalculatePopupDimensions(){
        const bpWrapper = document.querySelector('.kbe-booking-params-wrapper');
        const popupBaseContainer = document.querySelector('.kbe-header-occupancy-selector-wrapper div');
        if (!!bpWrapper && !!popupBaseContainer){
            const bpBoundings = bpWrapper.getBoundingClientRect();
            const popupBaseBoundings = popupBaseContainer.getBoundingClientRect();
            const leftOffset = Math.abs(popupBaseBoundings.left - bpBoundings.left) * -1;

            this.setState({
                popupStyle: {
                    width: bpBoundings.width + 'px',
                    maxWidth: bpBoundings.width + 'px',
                    left: leftOffset + 'px',
                },
            });
        }
    }

    toggleWindowResizeEventHandler(isOccupancyOpen) {
        const handleWindowResize = () => {
            this.recalculatePopupDimensions();
        };
        if (isOccupancyOpen) {
            window.addEventListener('resize', handleWindowResize);
        } else {
            window.removeEventListener('resize', handleWindowResize);
        }
    };

    /**
     * According to this variable, the room selector is shown and certain styles are added
     */
    showOccupancySelector(e, isActive) {
        e.preventDefault();
        this.setState({ isActive });
        this.recalculatePopupDimensions();
    }

    /**
     * The method reacts to the caller by considering whether the caller is inside the
     * OccupancySelector or the Trigger
     */
    handleClick(e) {
        const { isActive } = this.state;
        const [ wrapper, trigger ] = [ this.wrapperRef && this.wrapperRef.current, this.triggerRef && this.triggerRef.current ];

        // If the click occurs in an element contained by the wrapper of the occupancy
        // selector or the trigger, the corresponding action will be performed
        if (wrapper.contains(e.target) && !isActive) {
            this.showOccupancySelector(e, true);
        } else if ((!wrapper.contains(e.target) || trigger.contains(e.target)) && isActive) {
            this.showOccupancySelector(e, false);
        }
    }

    closeButton() {
        return (
            <div
                className="kbe-display-block kbe-text-right kbe-margin-bottom-medium"
                onClick={e => this.showOccupancySelector(e, false)}
                onKeyDown={e => this.showOccupancySelector(e, false)}
                role="button"
                tabIndex="0"
            >
                <Icon name="Close" fill="rgba(0, 0, 0, .4)" size="18" />
            </div>
        );
    }

    render() {
        const { isActive, popupStyle } = this.state;
        const { occupancy, container, childrenEnabled,
            maxChildAge, minChildAge, maxOccupancy, allowRooms,
            maxRoomsPerBooking, minOccupancy, onChangeRoomsQuantity,
            onRemoveRoom, onChangeAdults, onChangeChildren, onChangeChildrenAge } = this.props;
        const { id } = this.context;
        const { closeButton: CloseButton } = this;
        const roomListProps = {
            widgetId: id,
            occupancy,
            childrenEnabled,
            maxChildAge,
            minChildAge,
            maxOccupancy,
            maxRoomsPerBooking,
            allowRooms,
            minOccupancy,
            onChangeRoomsQuantity,
            onRemoveRoom,
            onChangeAdults,
            onChangeChildren,
            onChangeChildrenAge,
        };

        return (
            <OccupancySelector.Wrapper wrapperRef={this.wrapperRef} container={container}>
                <Trigger
                    occupancy={occupancy}
                    isOccupancyActive={isActive}
                    triggerRef={this.triggerRef}
                    allowRooms={allowRooms}
                />
                { isActive ?
                    <div className="kbe-dropdown-menu kbe-occupancy-selector-wrapper" style={popupStyle}>
                        { window.innerWidth < 639 && <CloseButton /> }
                        <RoomList {...roomListProps}/>
                    </div> : null}
            </OccupancySelector.Wrapper>
        );
    }
}

OccupancySelector.contextType = WidgetContext;
OccupancySelector.Wrapper = Wrapper;

OccupancySelector.defaultProps = {
    allowRooms: true,
};

OccupancySelector.propTypes = {
    container             : PropTypes.oneOfType([ PropTypes.instanceOf(Element), PropTypes.object ]).isRequired,
    childrenEnabled       : PropTypes.bool.isRequired,
    maxChildAge           : PropTypes.number.isRequired,
    minChildAge           : PropTypes.number.isRequired,
    maxOccupancy          : PropTypes.number.isRequired,
    maxRoomsPerBooking    : PropTypes.number.isRequired,
    minOccupancy          : PropTypes.number.isRequired,
    allowRooms            : PropTypes.bool,
    occupancy             : PropTypes.arrayOf(PropTypes.shape(occupancyShape)).isRequired,
    onChangeRoomsQuantity : PropTypes.func.isRequired,
    onRemoveRoom          : PropTypes.func.isRequired,
    onChangeAdults        : PropTypes.func.isRequired,
    onChangeChildren      : PropTypes.func.isRequired,
    onChangeChildrenAge   : PropTypes.func.isRequired,
};

export default OccupancySelector;
